// POM-6180 / POM-6190: confirm moving a whole order to Fill Later.
//
// One date for the order, no quantities to edit — every line item moves at its full quantity,
// which is the difference from the split dialog where something always stays to fill now. The
// dialog lists what will move so the user confirms the scope, and says plainly that the order is
// parked until the date: this is a whole-order action and is not undone from here.
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import { addDays, addYears, format, isValid, startOfDay } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import {
  FILL_LATER_DATE_WINDOW_MESSAGE,
  FILL_LATER_LEAD_TIME_DAYS,
} from 'common/constants/fillLater';
import FillLaterAddressField from 'common/components/FillLaterAddressField';
import useRecipientAddresses from 'common/hooks/useRecipientAddresses';
import NewPatientAddressButton from 'modules/PatientView/components/ShippingTab/NewPatientAddressButton';
import DateField from 'common/forms/DateField';
import PatientBanner from 'common/modules/PatientBanner';
import { moveOrderToFillLater } from 'store/thunks/patientThunks';
import { SubmitInfo, mutedLabel } from './SplitOrderByQuantityTiles';
import { describeLineItem } from './splitOrderByQuantityHelpers';

export const DATE_AFTER_TODAY_MESSAGE = FILL_LATER_DATE_WINDOW_MESSAGE;

const MoveOrderToFillLaterDialog = ({
  open,
  onClose,
  onSuccess,
  mpi,
  orderId,
  lineItems,
  orderAddress,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [addressDirectoryId, setAddressDirectoryId] = useState(null);
  const [addressBlocked, setAddressBlocked] = useState(false);
  const [manuallyVerified, setManuallyVerified] = useState(false);
  const {
    addresses,
    loading: addressesLoading,
    reload: reloadAddresses,
  } = useRecipientAddresses({
    orderId,
    enabled: open,
  });
  const storedAddressCount = useSelector(({ patient }) => patient?.[mpi]?.addresses?.length ?? 0);

  useEffect(() => {
    if (open && storedAddressCount) reloadAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedAddressCount]);

  const today = useMemo(() => startOfDay(new Date()), []);

  // The lead time SUGGESTS the date (POM-6197); the date must be strictly after today, which is
  // what moveOrderToFillLater enforces server side. The one-year cap is a UI guard only - without
  // it the picker runs to 2099, which is never a real fill date. Move items to Fill Later already
  // capped at a year, so this matches it rather than introducing a second convention.
  const suggestedDate = useMemo(() => addDays(today, FILL_LATER_LEAD_TIME_DAYS), [today]);
  const latestSelectableDate = useMemo(() => addYears(today, 1), [today]);

  const validateFillDate = (value) => {
    if (!value || !isValid(value)) return undefined;
    const chosen = startOfDay(value);

    return chosen <= today || chosen > latestSelectableDate
      ? FILL_LATER_DATE_WINDOW_MESSAGE
      : undefined;
  };

  const handleClose = (_, reason) => {
    // A whole-order action: neither a stray backdrop click nor a click mid-submit closes it.
    if (reason === 'backdropClick' || isLoading) return;
    onClose();
  };

  const handleSubmit = async ({ fillDate }) => {
    if (!fillDate || validateFillDate(fillDate)) return undefined;

    setIsLoading(true);

    const result = await dispatch(
      moveOrderToFillLater({
        mpi,
        orderId,
        scheduledFor: format(fillDate, 'yyyy-MM-dd'),
        addressDirectoryId,
        isManuallyVerified: manuallyVerified,
      })
    );

    setIsLoading(false);

    // The thunk reports failures through the shared error dialog and resolves undefined; the
    // dialog stays open so the user can retry or back out.
    if (!result) return undefined;

    enqueueSnackbar(`Order ${orderId} moved to Fill Later for ${format(fillDate, 'MM-dd-yyyy')}`, {
      variant: 'success',
    });
    onSuccess?.();
    onClose();

    return undefined;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle id='MoveOrderToFillLater-header' sx={{ pb: 1 }}>
        Move order to Fill Later
      </DialogTitle>
      <Form
        onSubmit={handleSubmit}
        initialValues={{ fillDate: suggestedDate }}
        render={({ handleSubmit: submitForm, invalid, values }) => (
          <form noValidate onSubmit={submitForm} data-testid='move-order-to-fill-later-form'>
            <DialogContent sx={{ pt: 0 }}>
              <Grid container direction='column' spacing={2}>
                <Grid item>
                  <PatientBanner mpi={mpi} />
                </Grid>

                <Grid item>
                  <Alert severity='warning' data-testid='move-order-warning'>
                    The whole order {orderId} will be parked. Nothing on it fills until the date
                    below, and every line item moves at its full quantity.
                  </Alert>
                </Grid>

                <Grid item>
                  <Typography sx={{ color: mutedLabel, fontSize: '0.85em', mb: 0.5 }}>
                    {lineItems.length === 1
                      ? '1 line item will move'
                      : `${lineItems.length} line items will move`}
                  </Typography>
                  <Divider />
                  <Box
                    sx={{
                      // The order can carry many line items; left to grow, the list pushes the
                      // footer off the screen and the buttons are only reachable after scrolling
                      // the whole dialog. The list scrolls instead.
                      maxHeight: 360,
                      overflowY: 'auto',
                    }}
                  >
                    {lineItems.map((item) => (
                      <Grid
                        container
                        key={item.lineItemId}
                        alignItems='center'
                        justifyContent='space-between'
                        sx={{ py: 0.75 }}
                        data-testid={`move-order-item-${item.lineItemId}`}
                      >
                        <Grid item xs>
                          <Typography sx={{ fontWeight: 500 }}>{describeLineItem(item)}</Typography>
                          <Typography sx={{ color: mutedLabel, fontSize: '0.85em' }}>
                            Rx {item.rxNumber ?? '—'}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography sx={{ color: mutedLabel, fontSize: '0.85em' }}>
                            Qty {item.quantity ?? '—'}
                          </Typography>
                        </Grid>
                      </Grid>
                    ))}
                  </Box>
                  <Divider />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DateField
                    name='fillDate'
                    id='MoveOrderToFillLater-fillDate'
                    label='Fill date'
                    required
                    // validateMinDate is strict (isAfter), so "today" as the floor admits
                    // tomorrow and refuses today - the same rule the server applies.
                    minDate={today}
                    maxDate={latestSelectableDate}
                    shouldDisableDate={(date) =>
                      startOfDay(date) <= today || startOfDay(date) > latestSelectableDate
                    }
                    validations={[validateFillDate]}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.5,
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ color: mutedLabel, fontSize: '0.8em', fontWeight: 600 }}>
                      One address for the whole order &mdash; every line item on it ships together.
                    </Typography>
                    <Box
                      sx={{
                        flexShrink: 0,
                        '& .MuiButton-root': {
                          py: 0.3,
                          px: 1.25,
                          minWidth: 0,
                          fontSize: '0.72rem',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      <NewPatientAddressButton mpi={mpi} showDeliveryInstructions={false}>
                        Add a new address
                      </NewPatientAddressButton>
                    </Box>
                  </Box>

                  <FillLaterAddressField
                    id='MoveOrderToFillLater-address'
                    addresses={addresses}
                    value={addressDirectoryId}
                    onChange={setAddressDirectoryId}
                    onBlockedChange={setAddressBlocked}
                    onAcknowledgedChange={setManuallyVerified}
                    inheritedAddress={orderAddress}
                    loading={addressesLoading}
                  />
                </Grid>

                <Grid item>
                  <SubmitInfo>
                    {values.fillDate &&
                    isValid(values.fillDate) &&
                    !validateFillDate(values.fillDate)
                      ? `Order ${orderId} will be released to fill on ${format(
                          values.fillDate,
                          'MM-dd-yyyy'
                        )}. Until then it is parked and shows as Fill Later.`
                      : 'Pick the date the order should fill.'}
                  </SubmitInfo>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button variant='outlined' color='secondary' onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              <LoadingButton
                variant='contained'
                color='primary'
                type='submit'
                loading={isLoading}
                disabled={invalid || !values.fillDate || addressBlocked}
              >
                Move to Fill Later
              </LoadingButton>
            </DialogActions>
          </form>
        )}
      />
    </Dialog>
  );
};

MoveOrderToFillLaterDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  mpi: PropTypes.string.isRequired,
  orderId: PropTypes.string.isRequired,
  orderAddress: PropTypes.shape({}),
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      lineItemId: PropTypes.string.isRequired,
      itemName: PropTypes.string,
      strength: PropTypes.string,
      form: PropTypes.string,
      rxNumber: PropTypes.string,
      quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
};

MoveOrderToFillLaterDialog.defaultProps = {
  onSuccess: null,
  orderAddress: null,
};

export default MoveOrderToFillLaterDialog;
